/**
 * Reusable live camera-capture modal.
 *
 * Requests the device camera via getUserMedia, shows a live preview, captures a
 * still frame to a JPEG, offers a retake/confirm review step, and returns the
 * confirmed frame as a `File` via `onCapture`. It is upload-agnostic — callers
 * pipe the File into whatever upload path they already use — so it drops into
 * any image-upload surface.
 *
 * getUserMedia requires a secure context (https, or http://localhost in dev).
 * Callers should gate their "Take photo" trigger on `isCameraCaptureSupported()`
 * so it's hidden where the camera can't work.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Modal } from '@helios/ui/overlay';
import { Button } from '@helios/ui/button';
import { NavIcon } from '../shell/shared';

export interface CameraCaptureModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Fires once the user confirms a captured frame. Receives a jpeg File. */
  onCapture: (file: File) => void;
  /** Base filename (no extension). A `-<timestamp>.jpg` suffix is appended. */
  filenamePrefix?: string;
  /** Preferred camera. Default 'environment' (rear). Passed as a soft ideal. */
  facingMode?: 'environment' | 'user';
  /** JPEG quality 0..1. Default 0.92. */
  quality?: number;
  title?: string;
  description?: string;
}

/** True when the camera can actually be used (API present + secure context). */
export function isCameraCaptureSupported(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    !!navigator.mediaDevices &&
    typeof navigator.mediaDevices.getUserMedia === 'function' &&
    typeof window !== 'undefined' &&
    window.isSecureContext === true
  );
}

type CamPhase = 'starting' | 'live' | 'captured' | 'error';

function messageForError(err: unknown): string {
  const name = err instanceof Error ? err.name : '';
  switch (name) {
    case 'NotAllowedError':
    case 'SecurityError':
      return 'Camera permission was denied. Enable camera access in your browser settings and try again.';
    case 'NotFoundError':
    case 'OverconstrainedError':
      return 'No camera was found on this device.';
    case 'NotReadableError':
    case 'AbortError':
      return 'The camera is in use by another app. Close it and try again.';
    default:
      return 'Could not start the camera. Please try again.';
  }
}

export function CameraCaptureModal({
  open,
  onOpenChange,
  onCapture,
  filenamePrefix = 'camera-capture',
  facingMode = 'environment',
  quality = 0.92,
  title = 'Take photo',
  description,
}: CameraCaptureModalProps) {
  const [phase, setPhase] = useState<CamPhase>('starting');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  // Bumped to re-acquire the stream after a retake.
  const [restartNonce, setRestartNonce] = useState(0);

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const capturedBlobRef = useRef<Blob | null>(null);
  const previewUrlRef = useRef<string | null>(null);

  const stopStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }, []);

  const revokePreview = useCallback(() => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
  }, []);

  // Acquire the camera when the modal opens (and again after a retake, via
  // restartNonce). Deliberately NOT keyed on `phase`: if it were, the
  // setPhase('live') below would re-run this effect and its cleanup would stop
  // the stream we just attached — a black preview. Cleanup stops tracks only
  // when the modal closes, the camera changes, or a retake bumps the nonce, so
  // the camera light never lingers.
  useEffect(() => {
    if (!open) return;

    let cancelled = false;

    if (!isCameraCaptureSupported()) {
      setErrorMessage('Camera is not available on this device or connection.');
      setPhase('error');
      return;
    }

    setPhase('starting');
    navigator.mediaDevices
      .getUserMedia({ video: { facingMode }, audio: false })
      .then((stream) => {
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        const video = videoRef.current;
        if (video) {
          video.srcObject = stream;
          // Some browsers don't autoplay a srcObject reliably; nudge it.
          void video.play().catch(() => {});
        }
        setPhase('live');
      })
      .catch((err) => {
        if (cancelled) return;
        console.warn('[CameraCapture] getUserMedia failed:', err);
        setErrorMessage(messageForError(err));
        setPhase('error');
      });

    return () => {
      cancelled = true;
      stopStream();
    };
  }, [open, facingMode, restartNonce, stopStream]);

  // Full teardown when the modal closes: stop the stream, drop any captured
  // frame, and reset to the initial phase for next open.
  useEffect(() => {
    if (open) return;
    stopStream();
    revokePreview();
    capturedBlobRef.current = null;
    setPreviewUrl(null);
    setErrorMessage(null);
    setPhase('starting');
  }, [open, stopStream, revokePreview]);

  const handleCapture = useCallback(() => {
    const video = videoRef.current;
    if (!video || !video.videoWidth || !video.videoHeight) return;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          setErrorMessage('Could not capture the photo. Please try again.');
          return;
        }
        capturedBlobRef.current = blob;
        revokePreview();
        const url = URL.createObjectURL(blob);
        previewUrlRef.current = url;
        setPreviewUrl(url);
        setPhase('captured');
        // Release the camera during review; retake re-acquires it.
        stopStream();
      },
      'image/jpeg',
      quality,
    );
  }, [quality, revokePreview, stopStream]);

  const handleRetake = useCallback(() => {
    revokePreview();
    setPreviewUrl(null);
    capturedBlobRef.current = null;
    setPhase('starting');
    setRestartNonce((n) => n + 1);
  }, [revokePreview]);

  const handleRetry = useCallback(() => {
    setErrorMessage(null);
    setPhase('starting');
    setRestartNonce((n) => n + 1);
  }, []);

  const handleUsePhoto = useCallback(() => {
    const blob = capturedBlobRef.current;
    if (!blob) return;
    const file = new File([blob], `${filenamePrefix}-${Date.now()}.jpg`, { type: 'image/jpeg' });
    onCapture(file);
    onOpenChange(false);
  }, [filenamePrefix, onCapture, onOpenChange]);

  const footer =
    phase === 'captured' ? (
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--sp-2)' }}>
        <Button
          variant="outline"
          size="md"
          leftIcon={<NavIcon name="refresh" />}
          onClick={handleRetake}
        >
          Retake
        </Button>
        <Button
          variant="gradient"
          size="md"
          leftIcon={<NavIcon name="check" />}
          onClick={handleUsePhoto}
        >
          Use photo
        </Button>
      </div>
    ) : phase === 'error' ? (
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--sp-2)' }}>
        <Button variant="outline" size="md" onClick={() => onOpenChange(false)}>
          Cancel
        </Button>
        <Button
          variant="gradient"
          size="md"
          leftIcon={<NavIcon name="refresh" />}
          onClick={handleRetry}
        >
          Try again
        </Button>
      </div>
    ) : (
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--sp-2)' }}>
        <Button variant="outline" size="md" onClick={() => onOpenChange(false)}>
          Cancel
        </Button>
        <Button
          variant="gradient"
          size="md"
          leftIcon={<NavIcon name="camera" />}
          disabled={phase !== 'live'}
          onClick={handleCapture}
        >
          Capture
        </Button>
      </div>
    );

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      description={description}
      maxWidth={560}
      footer={footer}
    >
      <div
        style={{
          position: 'relative',
          width: '100%',
          aspectRatio: '4 / 3',
          background: '#000',
          borderRadius: 'var(--radius-md)',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {/* Live preview — kept mounted so the ref exists when the stream
            attaches; hidden while reviewing a captured frame or on error. */}
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: phase === 'live' || phase === 'starting' ? 'block' : 'none',
          }}
        />

        {phase === 'starting' && (
          <div style={{ position: 'absolute', color: 'rgba(255,255,255,0.85)' }}>
            <NavIcon name="loader" /> Starting camera…
          </div>
        )}

        {phase === 'captured' && previewUrl && (
          <img
            src={previewUrl}
            alt="Captured"
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
        )}

        {phase === 'error' && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 'var(--sp-2)',
              padding: 'var(--sp-5)',
              textAlign: 'center',
              color: '#fff',
            }}
          >
            <NavIcon name="alert-triangle" />
            <span style={{ fontSize: 'var(--font-size-sm)' }}>{errorMessage}</span>
          </div>
        )}
      </div>
    </Modal>
  );
}
